import { currencyHelper } from "../utils/currency"
import { showToast } from "./toast_notification"
import { CalculateInsuranceFee } from "../../shared/utils";

export const OrderSummary = () => ({
  totalAmount: 0,
  discountAmount: 0,
  shippingFee: 0, // 新增運費屬性
  couponDiscount: 0, // 新增折價券折扣屬性
  couponType: '', // 折價券類型
  couponValue: 0, // 折價券值
  couponMaxDiscount: 0, // 最大折扣限制
  isLoading: true,
  currencySymbol: 'NT$',
  currencyCode: 'TWD',

  async init() {
    // 等待一下讓 Alpine store 初始化
    await this.$nextTick();

    // 設定載入狀態
    this.isLoading = true

    const { currencyCode, currencySymbol } = await currencyHelper.loadCurrencyInfo()
    this.currencyCode = currencyCode
    this.currencySymbol = currencySymbol

    try {
      // 從頁面上的資料元素獲取購物車總金額
      const cartTotalElement = document.querySelector('[data-cart-total]');
      if (cartTotalElement) {
        const cartTotal = parseFloat(cartTotalElement.dataset.cartTotal) || 0;
        // 初始化 store 的購物車資料
        if (this.$store.frontend && this.$store.frontend.setCart) {
          this.$store.frontend.setCart({
            totalSelectedAmount: cartTotal,
            items: []
          });
        }
      }

      // 初始化運費：檢查是否已有選擇的物流方式
      this.initializeShippingFee();

      // 初始化折價券折扣
      this.initializeCouponDiscount();
    } catch (error) {
      console.error('初始化購物車資料失敗：', error)
    } finally {
      this.isLoading = false
    }

    // 監聽運費更新事件
    document.addEventListener('shipping-fee-updated', (event) => {
      this.handleShippingFeeUpdate(event.detail);
    });

    // 監聽物流方式變更事件
    document.addEventListener('shipping-method-changed', (event) => {
      if (event.detail && typeof event.detail.shippingFee === 'number') {
        this.handleShippingFeeUpdate({ fee: event.detail.shippingFee });
      }
    });

    // 監聽折價券事件
    document.addEventListener('coupon-applied', (event) => {
      this.handleCouponApplied(event.detail);
    });

    document.addEventListener('coupon-removed', () => {
      this.handleCouponRemoved();
    });

    this.$watch('discountAmount', () => {
      this.updateTotalAmount()
    })

    this.$watch('shippingFee', () => {
      this.updateCouponDiscount()
      this.updateTotalAmount()
    })

    this.$watch('couponDiscount', () => {
      this.updateTotalAmount()
    })

    // 安全地監聽 store 變化
    this.$watch('$store.frontend?.cart?.totalSelectedAmount', () => {
      this.updateTotalAmount()
    })
  },

  // 初始化運費：檢查頁面上是否已有選擇的物流方式
  initializeShippingFee() {
    // 使用 setTimeout 確保 DOM 完全載入後再檢查
    setTimeout(() => {
      const shippingMethodSelect = document.querySelector('select[name="order[order_recipient_attributes][shipping_method]"]');
      if (shippingMethodSelect && shippingMethodSelect.value) {
        const shippingMethod = shippingMethodSelect.value;
        this.shippingFee = this.calculateShippingFee(shippingMethod);
      }
    }, 100);
  },

  // 初始化折價券折扣
  initializeCouponDiscount() {
    // 從頁面上獲取折價券資訊
    const element = this.$el;
    if (element) {
      this.couponDiscount = parseFloat(element.dataset.couponDiscount) || 0;
      this.couponType = element.dataset.couponType || '';
      this.couponValue = parseFloat(element.dataset.couponValue) || 0;
      this.couponMaxDiscount = parseFloat(element.dataset.couponMaxDiscount) || 0;

      if (this.couponType === 'fixed_amount') {
        this.couponValue = currencyHelper.convertPrice(this.couponValue)
      }
      if (this.couponMaxDiscount > 0) {
        this.couponMaxDiscount = currencyHelper.convertPrice(this.couponMaxDiscount)
      }
    }
  },

  // 更新折價券折扣（當運費變化時重新計算）
  updateCouponDiscount() {
    const shippingMethodSelect = document.querySelector('select[name="order[order_recipient_attributes][shipping_method]"]');

    // 檢查是否為海外宅配（服務型商品沒有運送方式選擇器）
    if (shippingMethodSelect && shippingMethodSelect.value == "overseas_delivery" && this.couponType == "free_shipping") {
      this.couponDiscount = 0
      return
    }

    if (!this.couponType) return;

    const cartTotal = this.$store.frontend?.cart?.totalSelectedAmount || 0;
    let newDiscount = 0;

    switch (this.couponType) {
      case 'fixed_amount':
        newDiscount = Math.min(this.couponValue, cartTotal);
        break;
      case 'percentage': {
        
        newDiscount = Math.round(cartTotal * (this.couponValue / 100.0));
        break;
      }
      case 'free_shipping':
        newDiscount = this.shippingFee;
        break;
      default:
        newDiscount = 0;
    }

    // 考慮最大折扣限制
    if (this.couponMaxDiscount > 0 && this.couponType !== 'free_shipping') {
      newDiscount = Math.min(newDiscount, this.couponMaxDiscount);
    }
    this.couponDiscount = newDiscount;
  },

  // 處理折價券套用事件
  handleCouponApplied(detail) {
    if (detail && detail.coupon) {
      this.couponType = detail.coupon.discount_type || '';
      this.couponValue = detail.coupon.discount_value || 0;
      this.couponMaxDiscount = detail.coupon.max_discount_amount || 0;
      
      const exchangeRate = this.$store.frontend?.cart?.exchangeRate || 1
      const precision = currencyHelper.getCurrencyPrecision(this.currencyCode)
      const roundFactor = Math.pow(10, precision)

      if (this.couponType === 'fixed_amount') {
        this.couponValue = Math.round(this.couponValue * exchangeRate * roundFactor) / roundFactor
      }
      if (this.couponMaxDiscount > 0) {
        this.couponMaxDiscount = Math.round(this.couponMaxDiscount * exchangeRate * roundFactor) / roundFactor
      }

      this.updateCouponDiscount();
    }
  },

  // 處理折價券移除事件
  handleCouponRemoved() {
    this.couponDiscount = 0;
    this.couponType = '';
    this.couponValue = 0;
    this.couponMaxDiscount = 0;
  },

  // 處理運費更新事件
  handleShippingFeeUpdate(detail) {
    if (detail && typeof detail.fee === 'number') {
      this.shippingFee = detail.fee;

      // 運費變化時重新計算折價券折扣（特別是免運費券）
      this.updateCouponDiscount();
    }
  },

  // 根據物流方式計算運費（使用多幣別對照）
  calculateShippingFee(shippingMethod) {
    const domesticFees = {
      convenience_store: { TWD: 60, HKD: 20, CNY: 20, JPY: 333, USD: 2 },
      home_delivery:     { TWD: 135, HKD: 45, CNY: 45, JPY: 750, USD: 5 }
    }

    switch (shippingMethod) {
      case 'convenience_store':
        return domesticFees.convenience_store[this.currencyCode] || 60;
      case 'home_delivery':
        return domesticFees.home_delivery[this.currencyCode] || 135;
      default:
        return 0;
    }
  },

  updateTotalAmount() {
    const cartTotal = this.$store.frontend?.cart?.totalSelectedAmount || 0
    this.totalAmount = Math.max(0, cartTotal + this.shippingFee - this.couponDiscount - this.discountAmount)
  },

  displayPrice(price, unit = null, skipConversion = false) {
    const numericPrice = Number(price || 0)
    const convertedPrice = skipConversion
      ? numericPrice
      : currencyHelper.convertPrice(numericPrice, 'TWD', this.currencyCode)
    const precision = currencyHelper.getCurrencyPrecision(this.currencyCode)

    const formattedValue = Number(convertedPrice || 0).toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    })

    const symbolToUse = unit || this.currencySymbol

    return `${symbolToUse} ${formattedValue}`.trim()
  },

  get finalAmount() {
    const cartTotal = Number(this.$store.frontend?.cart?.totalSelectedAmount || 0)
    const getInsurance = this.insuranceFee()

    return Math.max(0, cartTotal + this.shippingFee + getInsurance - this.couponDiscount - this.discountAmount)
  },

  effectiveCurrencyCode() {
    return this.currencyCode !== 'TWD' ? this.currencyCode : (currencyHelper.getDomCurrency() || 'TWD')
  },

  effectiveCurrencySymbol() {
    return this.currencySymbol !== 'NT$' ? this.currencySymbol : (currencyHelper.CURRENCY_SYMBOLS[this.effectiveCurrencyCode()] || 'NT$')
  },

  insuranceFee() {
    const country = this.$store.frontend.countryCode;
    const price = this.$store.frontend.cart.totalSelectedAmount
    const insuranceAdded = this.$store.frontend.insuranceSelected

    return insuranceAdded ? CalculateInsuranceFee(price, country, this.effectiveCurrencyCode()) : 0
  },

  displayInsuranceFee() {
    const fee = this.insuranceFee()
    const code = this.effectiveCurrencyCode()
    const precision = currencyHelper.getCurrencyPrecision(code)

    const formattedValue = Number(fee || 0).toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    })

    return `${this.effectiveCurrencySymbol()} ${formattedValue}`.trim()
  },

  // 新增訂單提交方法
  async submitOrder() {
    // 等待 Alpine.js 初始化
    await this.$nextTick()

    const validatorElement = document.querySelector('[x-ref=orderValidator]');
    if (!validatorElement) {
      console.error('Validator element not found')
      return
    }

    // 獲取 Alpine.js 驗證器
    let validator = validatorElement.__x?.$data;
    if (!validator) {
      // 如果直接獲取失敗，嘗試通過 Alpine.$data
      validator = window.Alpine.$data(validatorElement)
    }

    if (validator && validator.validateAll) {
      const isValid = validator.validateAll()

      if (!isValid) {
        // 使用簡潔的錯誤提示
        const errorSummary = validator.getErrorSummary();
        if (errorSummary) {
          // 使用美觀的 toast 通知
          showToast(errorSummary, 'warning');

          // 滾動到第一個錯誤欄位
          this.scrollToFirstError(validator);
        }
        return;
      }
    } else {
      console.error('Validator not available or validateAll method missing')
      return
    }

    // 提交表單
    const form = document.querySelector('#order-form');
    if (form) {
      form.submit();
    }
  },

  scrollToFirstError(validator) {
    // 滾動到第一個有錯誤的區塊
    let targetSection = null;

    if (validator.errors.recipient.length > 0) {
      targetSection = document.querySelector('[data-section="recipient"]') ||
        document.querySelector('h2:contains("購買人資訊")');
    } else if (validator.errors.shipping.length > 0) {
      targetSection = document.querySelector('[data-section="shipping"]') ||
        document.querySelector('h2:contains("物流")');
    } else if (validator.errors.invoice.length > 0) {
      targetSection = document.querySelector('[data-section="invoice"]') ||
        document.querySelector('h2:contains("付款資訊")');
    }

    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
})
